/**
 * TrainingList — Learning hub "Training" tab. Lists every non-archived
 * module with a "New module" CTA for managers and per-row status pills
 * for the current user (assigned / in progress / completed / competent).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  PlaySquare, Plus, Loader2, ArrowRight, Search, CheckCircle2, Clock, Award,
  LayoutDashboard, List,
} from "lucide-react";
import TrainingOverview from "@/components/learning/TrainingOverview";

export default function TrainingList() {
  const { user } = useAuth();
  const isManager = user && ["owner", "manager"].includes(user.role);
  const [rows, setRows] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [competencies, setCompetencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  // Managers get a "Team overview" view toggle alongside the module
  // list. Staff only ever see the list.
  const [view, setView] = useState("list"); // "list" | "overview"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rMod, rAssign, rComp] = await Promise.all([
        api.get("/training/modules"),
        api.get("/training/assignments", { params: { mine: true } }),
        api.get("/training/my-competencies"),
      ]);
      setRows(rMod.data || []);
      setAssignments(rAssign.data || []);
      setCompetencies(rComp.data || []);
    } catch (e) {
      toast.error("Load failed", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const assignmentByModule = useMemo(() => {
    const m = {};
    for (const a of assignments) m[a.module_id] = a;
    return m;
  }, [assignments]);

  const competentIds = useMemo(
    () => new Set(competencies.map((c) => c.module_id)),
    [competencies],
  );

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      (r.title || "").toLowerCase().includes(needle)
      || (r.category || "").toLowerCase().includes(needle),
    );
  }, [rows, q]);

  const visibleModuleIds = useMemo(() => new Set(rows.map((r) => r.id)), [rows]);
  const pendingCount = assignments.filter(
    (a) => a.status !== "completed" && visibleModuleIds.has(a.module_id),
  ).length;

  return (
    <div className="space-y-4" data-testid="training-list">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-zinc-600">
          Modules published by your managers. Read the material, then request a
          sign-off when you&apos;re ready.
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isManager && (
            <div className="inline-flex rounded-md border border-zinc-200 bg-white p-0.5" role="tablist" data-testid="training-view-toggle">
              <button
                role="tab"
                aria-selected={view === "list"}
                onClick={() => setView("list")}
                className={`text-xs px-2.5 py-1 rounded flex items-center gap-1 ${
                  view === "list" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"
                }`}
                data-testid="training-view-list"
              >
                <List className="h-3 w-3" /> Modules
              </button>
              <button
                role="tab"
                aria-selected={view === "overview"}
                onClick={() => setView("overview")}
                className={`text-xs px-2.5 py-1 rounded flex items-center gap-1 ${
                  view === "overview" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"
                }`}
                data-testid="training-view-overview"
              >
                <LayoutDashboard className="h-3 w-3" /> Team overview
              </button>
            </div>
          )}
          {isManager && view === "list" && (
            <Button
              onClick={() => setShowCreate(true)}
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="training-new-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> New module
            </Button>
          )}
        </div>
      </div>

      {isManager && view === "overview" ? (
        <TrainingOverview />
      ) : (
        <>
          {pendingCount > 0 && (
        <div
          className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 flex items-center gap-2 text-sm text-amber-900"
          data-testid="training-pending-banner"
        >
          <Clock className="h-4 w-4" />
          <span>
            You have <b>{pendingCount}</b> training module{pendingCount === 1 ? "" : "s"} outstanding.
          </span>
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-zinc-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search modules by title or category…"
          className="pl-8"
          data-testid="training-search"
        />
      </div>

      {loading ? (
        <div className="p-6 text-sm text-zinc-500 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="p-8 text-center border border-dashed border-zinc-200 rounded-md text-sm text-zinc-500">
          {rows.length === 0
            ? (isManager ? "No modules yet — click ‘New module’ to start." : "No training modules have been published yet.")
            : "No modules match your search."}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((m) => (
            <ModuleRow
              key={m.id}
              module={m}
              assignment={assignmentByModule[m.id]}
              competent={competentIds.has(m.id)}
            />
          ))}
        </div>
      )}

      {showCreate && (
        <NewModuleDialog
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
        </>
      )}
    </div>
  );
}


function ModuleRow({ module: mod, assignment, competent }) {
  const nav = useNavigate();
  const status = assignment?.status;
  const overdue = assignment?.due_at
    && assignment.status !== "completed"
    && new Date(assignment.due_at) < new Date();

  return (
    <button
      onClick={() => nav(`/learning/training/${mod.id}`)}
      className="block w-full text-left border border-zinc-200 rounded-md p-3 hover:border-zinc-400 hover:bg-zinc-50 transition-colors"
      data-testid={`training-row-${mod.id}`}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-zinc-950 flex items-center gap-2 flex-wrap">
            {mod.title}
            {mod.category && (
              <span className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 border border-zinc-200">
                {mod.category}
              </span>
            )}
            {(mod.media || []).length > 0 && (
              <span className="text-2xs uppercase tracking-wider text-zinc-500">
                {mod.media.length} attachment{mod.media.length === 1 ? "" : "s"}
              </span>
            )}
            {competent && (
              <span
                className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 inline-flex items-center gap-1"
                data-testid={`training-competent-${mod.id}`}
              >
                <Award className="h-3 w-3" /> competent
              </span>
            )}
            {!competent && status === "completed" && (
              <span className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 border border-zinc-200 inline-flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> completed
              </span>
            )}
            {!competent && status && status !== "completed" && (
              <span className={`text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border inline-flex items-center gap-1 ${
                overdue
                  ? "bg-red-50 text-red-800 border-red-200"
                  : "bg-amber-50 text-amber-900 border-amber-200"
              }`} data-testid={`training-status-${mod.id}`}>
                <Clock className="h-3 w-3" /> {status.replace("_", " ")}{overdue ? " · overdue" : ""}
              </span>
            )}
          </div>
          {mod.description && (
            <div className="text-xs text-zinc-600 mt-1 line-clamp-2">{mod.description}</div>
          )}
          {assignment?.due_at && (
            <div className="text-2xs text-zinc-500 mt-1">
              Due {new Date(assignment.due_at).toLocaleDateString()}
            </div>
          )}
        </div>
        <ArrowRight className="h-4 w-4 text-zinc-400 mt-1" />
      </div>
    </button>
  );
}


function NewModuleDialog({ onClose, onCreated }) {
  const nav = useNavigate();
  const [form, setForm] = useState({ title: "", category: "", description: "" });
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      const r = await api.post("/training/modules", {
        title: form.title.trim(),
        category: form.category.trim() || null,
        description: form.description,
        body_markdown: "",
      });
      toast.success("Module created — add content next.");
      onCreated?.(r.data);
      nav(`/learning/training/${r.data.id}`);
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="new-training-dialog">
        <DialogHeader>
          <DialogTitle>New training module</DialogTitle>
          <DialogDescription className="text-xs">
            Set the name and short description. You&apos;ll add markdown
            content and upload PDFs/videos on the next screen.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-2xs">Title</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Sharps disposal training"
              data-testid="new-training-title"
            />
          </div>
          <div>
            <Label className="text-2xs">Category</Label>
            <Input
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              placeholder="Clinical, WHS, Reception"
              data-testid="new-training-category"
            />
          </div>
          <div>
            <Label className="text-2xs">Short description</Label>
            <Textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="new-training-description"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-zinc-950 text-white" data-testid="new-training-save">
            {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</> : "Create module"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
